import { Subscription } from "../message/Subscription.js";
import { ValidUtils } from "../valid/ValidUtils.js";
import { AbortablePromise } from "./AbortablePromise.js";
var Promises;
((Promises2) => {
  Promises2.isFulfilled = (result) => {
    return result.status === "fulfilled";
  };
  Promises2.isRejected = (result) => {
    return result.status === "rejected";
  };
  Promises2.filterCatch = async (promise, has) => {
    try {
      await promise;
      return void 0;
    } catch (e) {
      const targetHas = Array.isArray(has) ? has : [has];
      for (let ha of targetHas) {
        if (ValidUtils.isFunctionType(ha)) {
          if (e instanceof ha) {
            return e;
          }
        } else {
          if (ha(e)) {
            return e;
          }
        }
      }
      throw e;
    }
    return void 0;
  };
  Promises2.sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  Promises2.sleepReject = (ms) => new Promise((resolve, reject) => setTimeout(reject, ms));
  Promises2.settle = async (promise) => {
    return Promise.allSettled([promise]).then((results) => results[0]);
  };
  Promises2.settles = async (...promises) => {
    return Promise.allSettled(promises);
  };
  Promises2.loop = (config) => {
    return {
      subscribe: (callback) => {
        let stop = false;
        let age = 0;
        const loopExecute = async () => {
          const start = Date.now();
          let end = start;
          age++;
          const executeConfig = { age };
          let t;
          let isCatch = false;
          try {
            t = await config.factory(executeConfig);
            end = Date.now();
            callback.then?.(t, { ...executeConfig, duration: end - start });
          } catch (e) {
            isCatch = true;
            end = Date.now();
            callback.catch?.(e, { ...executeConfig, duration: end - start });
          }
          await (0, Promises2.sleep)(config.loopDelay ?? 0);
          callback.delayThen?.({ ...executeConfig, duration: end - start, isCatch, data: t });
          if (!stop) return loopExecute();
        };
        setTimeout(() => {
          loopExecute();
        }, config.delay ?? 0);
        return new Subscription(() => {
          stop = true;
        });
      }
    };
  };
  Promises2.delayExecute = (value, delay = 0) => {
    return new Promise((resolve) => {
      setTimeout(async () => {
        const t = await value();
        resolve(t);
      }, delay);
    });
  };
  Promises2.abortable = (executor, signal) => {
    return new AbortablePromise(executor, signal);
  };
  Promises2.withResolvers = () => {
    let resolve;
    let reject;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
  Promises2.executeInChunks = async (promiseFactories, config) => {
    const results = [];
    const chunkSize = config.chunkSize;
    const sleepBetweenChunks = config.sleepBetweenChunks;
    for (let i = 0; i < promiseFactories.length; i += chunkSize) {
      const chunk = promiseFactories.slice(i, i + chunkSize);
      const chunkResults = await Promise.all(chunk.map((factory) => factory()));
      results.push(...chunkResults);
      if (i + chunkSize < promiseFactories.length) {
        await (0, Promises2.sleep)(sleepBetweenChunks);
      }
    }
    return results;
  };
  Promises2.executeSettledInChunks = async (promiseFactories, config) => {
    const results = [];
    const chunkSize = config.chunkSize;
    const sleepBetweenChunks = config.sleepBetweenChunks;
    for (let i = 0; i < promiseFactories.length; i += chunkSize) {
      const chunk = promiseFactories.slice(i, i + chunkSize);
      const chunkResults = await Promise.allSettled(chunk.map((factory) => factory()));
      results.push(...chunkResults);
      if (i + chunkSize < promiseFactories.length) {
        await (0, Promises2.sleep)(sleepBetweenChunks);
      }
    }
    return results;
  };
  Promises2.executeWithConcurrency = async (promiseFactories, concurrency = 10) => {
    const results = new Array(promiseFactories.length);
    let currentIndex = 0;
    const executeNext = async (index) => {
      while (currentIndex < promiseFactories.length) {
        const factoryIndex = currentIndex++;
        results[factoryIndex] = await promiseFactories[factoryIndex]();
      }
    };
    const workers = Array(Math.min(concurrency, promiseFactories.length)).fill(0).map((_, i) => executeNext(i));
    await Promise.all(workers);
    return results;
  };
  Promises2.executeWithLimit = async (promiseFactories, limit = 5) => {
    const results = new Array(promiseFactories.length);
    const executing = [];
    for (const [index, factory] of promiseFactories.entries()) {
      const promise = factory().then((result) => {
        results[index] = result;
      }).finally(() => {
        executing.splice(executing.indexOf(promise), 1);
      });
      executing.push(promise);
      if (executing.length >= limit) {
        await Promise.race(executing);
      }
    }
    await Promise.all(executing);
    return results;
  };
  Promises2.retry = async (factory, config) => {
    const initialDelay = config.delay?.initialDelay ?? 0;
    const retryDelay = config.delay?.retryDelay ?? 0;
    if (initialDelay > 0) {
      await (0, Promises2.sleep)(initialDelay);
    }
    let lastError;
    for (let attempt = 0; attempt <= config.retry; attempt++) {
      try {
        return await factory(attempt, lastError);
      } catch (error) {
        lastError = error;
        if (attempt === config.retry) {
          break;
        }
        config.onRetry?.(error, attempt + 1);
        if (retryDelay > 0) {
          await (0, Promises2.sleep)(retryDelay);
        }
      }
    }
    throw lastError;
  };
  let Result;
  ((Result2) => {
    async function awaitWrap(promise) {
      const data = typeof promise === "function" ? wrap(promise) : wrap(promise);
      try {
        await data;
      } catch (e) {
      }
      const rData = {
        status: data.status,
        isFulfilled: data.isFulfilled,
        isRejected: data.isRejected,
        isPending: data.isPending,
        value: data.value,
        reason: data.reason
      };
      if (typeof promise === "function") {
        rData.factory = () => {
          return awaitWrap(promise);
        };
        return rData;
      } else {
        return rData;
      }
    }
    Result2.awaitWrap = awaitWrap;
    function wrap(promise) {
      const promiseResult = typeof promise === "function" ? promise() : promise;
      if (typeof promise === "function") {
        const p = promiseResult;
        p.factory = () => {
          return wrap(promise);
        };
      }
      if (promiseResult.status === void 0) {
        const p = promiseResult;
        p.status = "pending";
        p.isPending = true;
        p.isFulfilled = false;
        p.isRejected = false;
        p.then(
          (result) => {
            console.log("result??", result);
            const p2 = promiseResult;
            p2.status = "fulfilled";
            p2.isFulfilled = true;
            p2.isRejected = false;
            p2.isPending = false;
            p2.value = result;
          },
          (reason) => {
            console.log("rejet??", reason);
            const p2 = promiseResult;
            p2.status = "rejected";
            p2.isFulfilled = false;
            p2.isRejected = true;
            p2.isPending = false;
            p2.reason = reason;
          }
        );
      }
      return promiseResult;
    }
    Result2.wrap = wrap;
  })(Result = Promises2.Result || (Promises2.Result = {}));
})(Promises || (Promises = {}));
export {
  Promises
};
//# sourceMappingURL=Promises.js.map
